/**
 * Structured Logging Utility for UsedFindr
 * 
 * Provides log levels, environment detection, and module-specific loggers
 * with structured data support for better debugging and production monitoring.
 * 
 * Usage:
 *   const logger = Logger.create('module-name');
 *   logger.debug('Debug info', { key: value });
 *   logger.info('Important event', { userId: 123 });
 *   logger.warn('Warning', { issue: 'timeout' });
 *   logger.error('Error occurred', { error, statusCode });
 */

class Logger {
  // Log levels
  static LEVELS = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3,
  };

  /**
   * Create a new logger instance for a specific module
   * @param {string} moduleName - Name of the module (e.g., 'api-service')
   * @param {Object} options - Configuration options
   * @param {boolean} options.silentPublicAuth - Suppress expected public auth errors
   */
  static create(moduleName, options = {}) {
    return new Logger(moduleName, options);
  }

  constructor(moduleName, options = {}) {
    this.moduleName = moduleName;
    this.options = options;
    this.minLevel = this._getMinLogLevel();
  }

  /**
   * Determine minimum log level based on environment
   * Checks DEBUG flag and environment variables
   */
  _getMinLogLevel() {
    // Check if we're in development mode
    const isDevelopment = this._isDevelopmentMode();
    
    // In development, show all logs (DEBUG and above)
    // In production, only show INFO and above
    return isDevelopment ? Logger.LEVELS.DEBUG : Logger.LEVELS.INFO;
  }

  /**
   * Detect if we're in development mode
   * Checks multiple signals: DEBUG flag, hostname, etc.
   */
  _isDevelopmentMode() {
    // Check for explicit DEBUG flag in localStorage or window
    if (typeof window !== 'undefined') {
      // Check localStorage for DEBUG flag
      try {
        const debugFlag = localStorage.getItem('DEBUG');
        if (debugFlag === 'true' || debugFlag === '1') return true;
        if (debugFlag === 'false' || debugFlag === '0') return false;
      } catch (e) {
        // localStorage may not be available
      }

      // Check window.DEBUG
      if (typeof window.DEBUG !== 'undefined') {
        return Boolean(window.DEBUG);
      }

      // Check hostname (localhost = development)
      const hostname = window.location?.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '') {
        return true;
      }

      // Check for file:// protocol (local development)
      if (window.location?.protocol === 'file:') {
        return true;
      }
    }

    // Default to production mode for safety
    return false;
  }

  /**
   * Format log message with module prefix and structured data
   */
  _formatMessage(level, message, data = {}) {
    const prefix = `[${this.moduleName}]`;
    const levelStr = Object.keys(Logger.LEVELS).find(
      key => Logger.LEVELS[key] === level
    );
    
    // Format: [module] message
    let formattedMsg = `${prefix} ${message}`;
    
    // Add data if provided
    if (data && Object.keys(data).length > 0) {
      return [formattedMsg, data];
    }
    
    return [formattedMsg];
  }

  /**
   * Check if a log level should be output
   */
  _shouldLog(level) {
    return level >= this.minLevel;
  }

  /**
   * DEBUG: Development-only detailed information
   * Suppressed in production
   */
  debug(message, data = {}) {
    if (!this._shouldLog(Logger.LEVELS.DEBUG)) return;
    
    const args = this._formatMessage(Logger.LEVELS.DEBUG, message, data);
    console.log(...args);
  }

  /**
   * INFO: General informational messages
   * Important state changes, successful operations
   */
  info(message, data = {}) {
    if (!this._shouldLog(Logger.LEVELS.INFO)) return;
    
    const args = this._formatMessage(Logger.LEVELS.INFO, message, data);
    console.log(...args);
  }

  /**
   * WARN: Warning messages for recoverable issues
   * Fallbacks used, potential problems
   */
  warn(message, data = {}) {
    if (!this._shouldLog(Logger.LEVELS.WARN)) return;
    
    const args = this._formatMessage(Logger.LEVELS.WARN, message, data);
    console.warn(...args);
  }

  /**
   * ERROR: Error messages for failures
   * Always logged regardless of level
   */
  error(message, data = {}) {
    if (!this._shouldLog(Logger.LEVELS.ERROR)) return;
    
    const args = this._formatMessage(Logger.LEVELS.ERROR, message, data);
    console.error(...args);
  }

  /**
   * Group logs together (useful for complex operations)
   */
  group(label) {
    if (this._shouldLog(Logger.LEVELS.DEBUG)) {
      console.group(`[${this.moduleName}] ${label}`);
    }
  }

  groupEnd() {
    if (this._shouldLog(Logger.LEVELS.DEBUG)) {
      console.groupEnd();
    }
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.Logger = Logger;
  // Make logger available immediately for scripts that load before this module
  if (!window.logger) {
    window.logger = Logger.create('global');
  }
}

// Also support ES6 module export for future use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Logger;
}
